import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button, Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { AiConversationListItem, AiConversationSections, AiProject } from '@/lib/models';
import { aiConversationService, aiProjectService } from '@/lib/services';
import { createUseThemedStyles, useAccount, useHandleError, useNavigationConfig } from '@/hooks';
import {
	AiExampleQuestion,
	AiNavigationButton,
	AiRateModal,
	AiSearchModal,
	AiSettingsModal,
	AiTextInputModal,
	ConfirmDialog,
} from '@/components';

import {
	ArrowReturnRight,
	Folder,
	FolderPlus,
	Gear,
	PencilSquare,
	Play,
	PlusCircleFill,
	QuestionCircleFill,
	Robot,
	Search,
	Trash3,
	XLg,
} from 'react-bootstrap-icons';
import { AiAddToProjectModal } from '@/components/ai-add-to-project-modal';
import { AiChat, AiChatRef } from '@/components/ai-chat';
import { isAskHackettSourceSelectionLocked } from '@/lib/models';

const useStyles = createUseThemedStyles((theme) => ({
	chatOuter: {
		display: 'flex',
		overflow: 'hidden',
		height: 'calc(100vh - 60px)',
	},
	chatList: {
		width: 296,
		flexShrink: 0,
		display: 'flex',
		flexDirection: 'column',
		backgroundColor: theme.colors.gray200,
		borderRight: `1px solid ${theme.colors.gray300}`,
	},
	chatListHeader: {
		padding: '30px 12px 0',
	},
	chatListScroller: {
		flex: 1,
		overflowY: 'auto',
		padding: '15px 15px 10px',
		'& h6': {
			padding: '0 10px',
		},
	},
	aiChatOuter: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		backgroundColor: theme.colors.white,
	},
	chatListFooter: {
		padding: '0 12px 20px 20px',
		backgroundColor: theme.colors.gray200,
	},
	inquiryCta: {
		padding: 10,
		display: 'flex',
		borderRadius: 6,
		backgroundColor: theme.colors.white,
		border: `1px solid ${theme.colors.gray300}`,
	},
	chatContent: {
		marginBottom: 30,
		'&:last-child': {
			marginBottom: 0,
		},
	},
	aiIconOuterHeader: {
		width: 28,
		flexShrink: 0,
		marginRight: 10,
	},
	aiIcon: {
		width: '100%',
		borderRadius: '50%',
		position: 'relative',
		paddingBottom: '100%',
		backgroundColor: theme.colors.primary,
		'& > svg': {
			top: '50%',
			left: '50%',
			position: 'absolute',
			transform: 'translate(-50%, -50%)',
		},
	},
	videoButton: {
		padding: 10,
		borderRadius: 500,
		position: 'relative',
		'&:after': {
			top: -2,
			left: -2,
			right: -2,
			bottom: -2,
			content: '""',
			position: 'absolute',
			borderRadius: 'inherit',
			boxShadow: `0 0 0 5px ${theme.colors.primary}`,
			animation: 'pulse 2000ms infinite cubic-bezier(.68,-0.55,.27,1.55)',
		},
	},
	'@global': {
		'@keyframes think': {
			'0%': {
				backgroundPosition: '-48px 0%',
			},
			'100%': {
				backgroundPosition: 'calc(100% + 48px) 0%',
			},
		},
		'@keyframes pulse': {
			'0%': {
				boxShadow: `0 0 0 4px ${theme.colors.primary}`,
			},
			'20%': {
				boxShadow: `0 0 0 8px ${theme.colors.primary}`,
			},
			'100%': {
				boxShadow: `0 0 0 4px ${theme.colors.primary}`,
			},
		},
	},
}));

export const AskHackett = () => {
	const { account } = useAccount();
	const { navigationConfig } = useNavigationConfig();
	const [searchParams, setSearchParams] = useSearchParams();
	const aiProjectIdFromQuery = useMemo(() => searchParams.get('aiProjectId'), [searchParams]);
	const aiConversationIdFromQuery = useMemo(() => searchParams.get('aiConversationId'), [searchParams]);
	const hasInquiryAccess = Boolean(
		navigationConfig?.allowClientManagement || navigationConfig?.allowAccessToInquiries
	);
	const inquiryCtaLink = navigationConfig?.allowClientManagement
		? '/client-management/inquiries-dashboard'
		: hasInquiryAccess
		? '/inquiries'
		: '/inquiries/learn-more';
	const inquiryCtaLinkText = hasInquiryAccess ? 'Submit an inquiry' : 'Learn more about Expert Inquiries';
	const inquiryCtaSuffix = hasInquiryAccess
		? "and we'll get back to you."
		: 'to see how your organization can request access.';
	const shouldHideAskHackettSettings = isAskHackettSourceSelectionLocked(
		account?.availableAiResponseSourceIds ?? []
	);

	const [showChatConfigModal, setShowChatConfigModal] = useState(false);

	const classes = useStyles();
	const handleError = useHandleError();

	const aiChatRef = useRef<AiChatRef>(null);

	const [aiProjects, setAiProjects] = useState<AiProject[]>([]);
	const [conversationsLoading, setConversationsLoading] = useState(false);
	const [conversationSections, setConversationSections] = useState<AiConversationSections>();

	const [showRateModel, setShowRateModal] = useState(false);
	const [queryIdToRate, setQueryIdToRate] = useState<string>();

	const [conversationToRename, setConversationToRename] = useState<AiConversationListItem>();
	const [conversationToProject, setConversationToProject] = useState<AiConversationListItem>();
	const [conversationToDelete, setConversationToDelete] = useState<AiConversationListItem>();

	const [explainerVideoUrl, setExplainerVideoUrl] = useState('');
	const [showAiSearchModal, setShowAiSearchModal] = useState(false);

	const [projectsLoading, setProjectsLoading] = useState(false);
	const [showNewProjectModal, setShowNewProjectModal] = useState(false);
	const [projectToRename, setProjectToRename] = useState<AiProject>();
	const [projectToDelete, setProjectToDelete] = useState<AiProject>();

	/*------------------------------------------------------*/
	/* Past conversations data fetch */
	/*------------------------------------------------------*/
	const fetchPastConversations = useCallback(async () => {
		setConversationsLoading(true);

		try {
			const response = await aiConversationService.getPastConversations().fetch();
			setConversationSections(response.aiConversations);
			setExplainerVideoUrl(response.explainerVideoUrl);
		} catch (error) {
			handleError(error);
		} finally {
			setConversationsLoading(false);
		}
	}, [handleError]);

	useEffect(() => {
		fetchPastConversations();
	}, [fetchPastConversations]);

	/*------------------------------------------------------*/
	/* Ai Projects data fetch */
	/*------------------------------------------------------*/
	const fetchAiProjects = useCallback(async () => {
		setProjectsLoading(true);

		try {
			const response = await aiProjectService.getProjects().fetch();
			setAiProjects(response.aiProjects);
		} catch (error) {
			handleError(error);
		} finally {
			setProjectsLoading(false);
		}
	}, [handleError]);

	useEffect(() => {
		fetchAiProjects();
	}, [fetchAiProjects]);

	const handleNewProjectModalSubmit = useCallback(
		async (value: string) => {
			setProjectsLoading(true);

			try {
				const response = await aiProjectService.createProject({ title: value }).fetch();
				await fetchAiProjects();

				aiChatRef.current?.reset();
				setShowNewProjectModal(false);

				searchParams.set('aiProjectId', response.aiProject.aiProjectId);
				searchParams.delete('aiConversationId');
				setSearchParams(searchParams);
			} catch (error) {
				handleError(error);
			} finally {
				setProjectsLoading(false);
			}
		},
		[fetchAiProjects, handleError, searchParams, setSearchParams]
	);

	const handleRenameProjectModalSubmit = useCallback(
		async (value: string) => {
			setProjectsLoading(true);

			try {
				if (!projectToRename?.aiProjectId) {
					throw new Error('projectToRename.aiProjectId is undefined.');
				}

				await aiProjectService.updateProject(projectToRename.aiProjectId, { title: value }).fetch();
				await fetchAiProjects();

				setProjectToRename(undefined);
			} catch (error) {
				handleError(error);
			} finally {
				setProjectsLoading(false);
			}
		},
		[fetchAiProjects, handleError, projectToRename?.aiProjectId]
	);

	const handleDeleteProjectModalSubmit = useCallback(async () => {
		setProjectsLoading(true);

		try {
			if (!projectToDelete?.aiProjectId) {
				throw new Error('projectToRename.aiProjectId is undefined.');
			}

			await aiProjectService.deleteProject(projectToDelete.aiProjectId).fetch();
			await fetchAiProjects();

			setProjectToDelete(undefined);
		} catch (error) {
			handleError(error);
		} finally {
			setProjectsLoading(false);
		}
	}, [fetchAiProjects, handleError, projectToDelete?.aiProjectId]);

	const handleRemoveFromProjectButtonClick = useCallback(
		async (aiProjectId: string, aiConversationId: string) => {
			setProjectsLoading(true);

			try {
				await aiProjectService.removeConversationFromProject(aiProjectId, { aiConversationId }).fetch();
				await Promise.all([fetchPastConversations(), fetchAiProjects()]);
			} catch (error) {
				handleError(error);
			} finally {
				setProjectsLoading(false);
			}
		},
		[fetchAiProjects, fetchPastConversations, handleError]
	);

	/*------------------------------------------------------*/
	/* Side bar click handlers */
	/*------------------------------------------------------*/
	const handleNewConversationButtonClick = async () => {
		aiChatRef.current?.reset();

		searchParams.delete('aiProjectId');
		searchParams.delete('aiConversationId');
		setSearchParams(searchParams);
	};

	const handleProjectButtonClick = async (aiProjectId: string) => {
		aiChatRef.current?.reset();

		searchParams.set('aiProjectId', aiProjectId);
		searchParams.delete('aiConversationId');
		setSearchParams(searchParams);
	};

	const handlePastConversationButtonClick = async (aiConversationId: string) => {
		searchParams.delete('aiProjectId');
		searchParams.set('aiConversationId', aiConversationId);
		setSearchParams(searchParams);
	};

	const handleDeletConversationModalSubmit = async () => {
		setConversationsLoading(true);

		try {
			if (!conversationToDelete) {
				throw new Error('conversationToDelete is undefined.');
			}

			await aiConversationService.deleteConversation(conversationToDelete.aiConversationId).fetch();
			if (aiConversationIdFromQuery === conversationToDelete.aiConversationId) {
				await handleNewConversationButtonClick();
			}
			await Promise.all([fetchPastConversations(), fetchAiProjects()]);

			setConversationToDelete(undefined);
		} catch (error) {
			handleError(error);
		} finally {
			setConversationsLoading(false);
		}
	};

	/*------------------------------------------------------*/
	/* Past conversation data fetch */
	/*------------------------------------------------------*/
	const fetchConversationById = useCallback(async () => {
		if (!aiConversationIdFromQuery) {
			return;
		}

		try {
			const response = await aiConversationService.getConversation(aiConversationIdFromQuery).fetch();
			aiChatRef.current?.reset({
				responses: response.aiConversation.conversationQueries.map((conversationQuery) => ({
					id: conversationQuery.conversationQueryId,
					aiConversationId: conversationQuery.aiConversationId,
					conversationQueryId: conversationQuery.conversationQueryId,
					question: conversationQuery.query,
					markdown: conversationQuery.queryResponse ?? '',
					typeOfData: '',
					streamStatus: conversationQuery.streamStatus,
				})),
				aiResponseSourceIds: response.aiConversation.conversationQueries[0].aiResponseSourceIds,
			});
		} catch (error) {
			handleError(error);
		}
	}, [aiConversationIdFromQuery, handleError]);

	useEffect(() => {
		fetchConversationById();
	}, [fetchConversationById]);

	const handleRateModalSave = () => {
		setShowRateModal(false);

		console.log('[TODO]: update markdown content to rated:true');

		setQueryIdToRate(undefined);
	};

	const handleRenameChatModalSubmit = useCallback(
		async (value: string) => {
			setConversationsLoading(true);

			try {
				if (!conversationToRename?.aiConversationId) {
					throw new Error('conversationToRename?.aiConversationId is undefined.');
				}

				await aiConversationService
					.updateConversation(conversationToRename.aiConversationId, { title: value })
					.fetch();
				await Promise.all([fetchPastConversations(), fetchAiProjects()]);

				setConversationToRename(undefined);
			} catch (error) {
				handleError(error);
			} finally {
				setConversationsLoading(false);
			}
		},
		[conversationToRename?.aiConversationId, fetchAiProjects, fetchPastConversations, handleError]
	);

	const aiProjectTitle = aiProjects.find((i) => i.aiProjectId === aiProjectIdFromQuery)?.title;

	return (
		<>
			<Helmet>
				<title>Hackett Connect - AskHackett</title>
			</Helmet>

			<AiSearchModal
				onAiConversationClick={(aiConversation) => {
					setShowAiSearchModal(false);
					handlePastConversationButtonClick(aiConversation.aiConversationId);
				}}
				show={showAiSearchModal}
				onHide={() => {
					setShowAiSearchModal(false);
				}}
			/>

			{queryIdToRate && (
				<AiRateModal
					conversationQueryId={queryIdToRate}
					show={showRateModel}
					onHide={() => {
						setShowRateModal(false);
						setQueryIdToRate(undefined);
					}}
					onSave={handleRateModalSave}
				/>
			)}

			<AiSettingsModal
				show={showChatConfigModal}
				onHide={() => {
					setShowChatConfigModal(false);
				}}
			/>

			<AiTextInputModal
				isLoading={projectsLoading || conversationsLoading}
				show={showNewProjectModal}
				title="New Project"
				label="Project Name"
				placeholder="E.g. Example"
				confirmButtonTitle="Create Project"
				onHide={() => {
					setShowNewProjectModal(false);
				}}
				onSubmit={handleNewProjectModalSubmit}
			/>

			<AiTextInputModal
				isLoading={projectsLoading || conversationsLoading}
				initialValue={projectToRename?.title}
				show={!!projectToRename}
				title="Rename Project"
				label="Project Name"
				placeholder="E.g. Example"
				confirmButtonTitle="Rename Project"
				onHide={() => {
					setProjectToRename(undefined);
				}}
				onSubmit={handleRenameProjectModalSubmit}
			/>

			<ConfirmDialog
				isLoading={projectsLoading || conversationsLoading}
				show={!!projectToDelete}
				onHide={() => {
					setProjectToDelete(undefined);
				}}
				onConfirm={handleDeleteProjectModalSubmit}
				title="Delete Project"
				confirmText="Delete Project"
				cancelText="Cancel"
				disabled={projectsLoading}
			>
				<p className="mb-0">
					<strong>This will permanently delete all project files and chats</strong>
				</p>
				<p className="mb-0">To save chats, move them to your chat list or another project before deleting.</p>
			</ConfirmDialog>

			<AiTextInputModal
				isLoading={projectsLoading || conversationsLoading}
				initialValue={conversationToRename?.title}
				show={!!conversationToRename}
				title="Rename Chat"
				label="Chat Name"
				confirmButtonTitle="Rename"
				onHide={() => {
					setConversationToRename(undefined);
				}}
				onSubmit={handleRenameChatModalSubmit}
			/>

			{conversationToProject && (
				<AiAddToProjectModal
					isLoading={projectsLoading || conversationsLoading}
					aiConversationId={conversationToProject.aiConversationId}
					show={!!conversationToProject}
					onHide={() => {
						setConversationToProject(undefined);
					}}
					onSubmit={() => {
						fetchAiProjects();
						fetchPastConversations();
						setConversationToProject(undefined);
					}}
				/>
			)}

			<ConfirmDialog
				isLoading={projectsLoading || conversationsLoading}
				show={!!conversationToDelete}
				onHide={() => {
					setConversationToDelete(undefined);
				}}
				onConfirm={handleDeletConversationModalSubmit}
				title="Delete Chat"
				confirmText="Delete Chat"
				cancelText="Cancel"
				disabled={projectsLoading}
			>
				<p className="mb-0">
					This will delete <strong>{conversationToDelete?.title}</strong>.
				</p>
			</ConfirmDialog>

			<div className={classes.chatOuter}>
				<div className={classes.chatList}>
					<div className={classes.chatListHeader}>
						<div className="mb-3 pl-1 d-flex align-items-center justify-content-between">
							<div className="d-flex align-items-center">
								<div className={classes.aiIconOuterHeader}>
									<div className={classes.aiIcon}>
										<Robot className="text-white" width={16} height={16} />
									</div>
								</div>
								<h4 className="mb-0 text-primary">AskHackett&trade;</h4>
							</div>
							{!shouldHideAskHackettSettings && (
								<Button
									aria-label="AskHackett Settings"
									className="p-2 pill"
									variant="link text-primary"
									onClick={() => {
										setShowChatConfigModal(true);
									}}
								>
									<Gear className="d-flex" width={20} height={20} />
								</Button>
							)}
						</div>
						<AiNavigationButton
							id="new-chat"
							variant="primary"
							icon={PlusCircleFill}
							title="New Chat"
							onClick={handleNewConversationButtonClick}
						/>
						<AiNavigationButton
							id="search-chats"
							className="mb-3"
							icon={Search}
							title="Search Chats"
							onClick={() => {
								setShowAiSearchModal(true);
							}}
						/>
						<hr />
					</div>
					<div className={classes.chatListScroller}>
						<div className="mb-3">
							<AiNavigationButton
								id="new-project"
								icon={FolderPlus}
								title="New Project"
								disabled={projectsLoading}
								onClick={() => {
									setShowNewProjectModal(true);
								}}
							/>
							{aiProjects.map((aiProject) => (
								<React.Fragment key={aiProject.aiProjectId}>
									<AiNavigationButton
										id={aiProject.aiProjectId}
										icon={Folder}
										title={aiProject.title}
										disabled={projectsLoading}
										onClick={() => {
											handleProjectButtonClick(aiProject.aiProjectId);
										}}
										count={
											aiProject.aiConversations.length > 0
												? aiProject.aiConversations.length
												: undefined
										}
										active={aiProject.aiProjectId === aiProjectIdFromQuery}
										menuItems={
											<>
												<Dropdown.Item
													as="button"
													onClick={() => {
														setProjectToRename(aiProject);
													}}
												>
													<PencilSquare size={16} className="mr-3" />
													Rename Project
												</Dropdown.Item>
												<Dropdown.Divider />
												<Dropdown.Item
													as="button"
													className="text-danger"
													onClick={() => {
														setProjectToDelete(aiProject);
													}}
												>
													<Trash3 size={16} className="text-danger mr-3" />
													<span className="font-weight-bold">Delete Project</span>
												</Dropdown.Item>
											</>
										}
									>
										{aiProject.aiConversations.map((i) => (
											<AiNavigationButton
												key={i.aiConversationId}
												id={i.aiConversationId}
												title={i.title}
												onClick={() => handlePastConversationButtonClick(i.aiConversationId)}
												disabled={projectsLoading || conversationsLoading}
												active={i.aiConversationId === aiConversationIdFromQuery}
												icon={ArrowReturnRight}
												indent
												menuItems={
													<>
														<Dropdown.Item
															as="button"
															onClick={() => {
																setConversationToRename(i);
															}}
														>
															<PencilSquare size={16} className="mr-3" />
															Rename
														</Dropdown.Item>
														<Dropdown.Item
															as="button"
															onClick={() => {
																setConversationToProject(i);
															}}
														>
															<Folder size={16} className="mr-3" />
															Move to Project
														</Dropdown.Item>
														<Dropdown.Item
															as="button"
															onClick={() => {
																handleRemoveFromProjectButtonClick(
																	aiProject.aiProjectId,
																	i.aiConversationId
																);
															}}
														>
															<XLg size={16} className="mr-3" />
															Remove from Project
														</Dropdown.Item>
														<Dropdown.Divider />
														<Dropdown.Item
															className="text-danger"
															onClick={() => {
																setConversationToDelete(i);
															}}
														>
															<Trash3 size={16} className="text-danger mr-3" />
															<span className="font-weight-bold">Delete</span>
														</Dropdown.Item>
													</>
												}
											/>
										))}
									</AiNavigationButton>
								</React.Fragment>
							))}
						</div>
						<hr className="mb-6" />
						{Object.entries(conversationSections ?? {}).map(([key, value]) => (
							<section key={key}>
								<h6 className="mb-2 text-primary">{key}</h6>
								<div className="mb-4">
									{value.map((c) => (
										<AiNavigationButton
											key={c.aiConversationId}
											id={c.aiConversationId}
											title={c.title}
											onClick={() => handlePastConversationButtonClick(c.aiConversationId)}
											disabled={conversationsLoading}
											active={c.aiConversationId === aiConversationIdFromQuery}
											menuItems={
												<>
													<Dropdown.Item
														as="button"
														onClick={() => {
															setConversationToRename(c);
														}}
													>
														<PencilSquare size={16} className="mr-3" />
														Rename
													</Dropdown.Item>
													<Dropdown.Item
														as="button"
														onClick={() => {
															setConversationToProject(c);
														}}
													>
														<Folder size={16} className="mr-3" />
														Add to Project
													</Dropdown.Item>
													<Dropdown.Divider />
													<Dropdown.Item
														className="text-danger"
														onClick={() => {
															setConversationToDelete(c);
														}}
													>
														<Trash3 size={16} className="text-danger mr-3" />
														<span className="font-weight-bold">Delete</span>
													</Dropdown.Item>
												</>
											}
										/>
									))}
								</div>
							</section>
						))}
					</div>
					{navigationConfig && !navigationConfig.inquiriesDisabled && hasInquiryAccess && (
						<div className={classes.chatListFooter}>
							<div className={classes.inquiryCta}>
								<QuestionCircleFill className="mt-1 mr-2 flex-shrink-0 text-primary" size={16} />
								<p className="m-0 small">
									Didn't find what you're looking for?{' '}
									<Link to={inquiryCtaLink}>{inquiryCtaLinkText}</Link> {inquiryCtaSuffix}
								</p>
							</div>
						</div>
					)}
				</div>
				<div className={classes.aiChatOuter}>
					<AiChat
						ref={aiChatRef}
						onQuerySubmitted={() => {
							fetchAiProjects();
							fetchPastConversations();
						}}
						noConversationState={
							<>
								{aiProjectIdFromQuery ? (
									<div className="py-8">
										<h2 className="text-center text-primary">
											<Folder size={32} className="mr-3" style={{ marginTop: '-0.4rem' }} />
											{aiProjectTitle}
										</h2>
									</div>
								) : (
									<>
										<div className="mb-6 d-flex align-items-center justify-content-between">
											<h2 className="mb-0 text-primary">
												Welcome to AskHackett<sup>&trade;</sup>
											</h2>
											{explainerVideoUrl && (
												<OverlayTrigger
													overlay={
														<Tooltip>
															<p className="mb-0 small">Watch Introduction Video</p>
														</Tooltip>
													}
												>
													<Button
														className={classes.videoButton}
														onClick={() => {
															window.open(explainerVideoUrl, '_blank', 'noopener');
														}}
													>
														<Play size={20} />
													</Button>
												</OverlayTrigger>
											)}
										</div>
										<p className="mb-3">
											AskHackett&trade; is our new AI-powered research assistant which provides
											instant self-service access to{' '}
											<span className="text-primary">Hackett Intelligence</span> - our proprietary
											research, enterprise benchmarks and transformation expertise trusted by the
											world's best businesses. Ask a question to{' '}
											<span className="text-primary">
												receive fast, real-world, fact-based business insights
											</span>{' '}
											that help leaders evaluate complex issues and identify proven approaches to
											move forward with confidence.
										</p>
										<p className="mb-6">
											AskHackett leverages advanced language models to generate responses to your
											research prompts using the{' '}
											<span className="text-primary">Hackett Intelligence</span> included in your
											membership program(s). Topics outside your program's areas of coverage are
											not supported.
										</p>
										<p className="mb-3">
											<strong>Example Prompts</strong>
										</p>
										<AiExampleQuestion
											className="mb-2"
											question="How do Digital World Class organizations outperform their peers?"
											onClick={(s) => {
												aiChatRef.current?.setMessage(s);
											}}
										/>
										<AiExampleQuestion
											className="mb-2"
											question="What are ways to optimize the purchase to pay process?"
											onClick={(s) => {
												aiChatRef.current?.setMessage(s);
											}}
										/>
										<AiExampleQuestion
											className="mb-2"
											question="What are emerging issues for the finance function?"
											onClick={(s) => {
												aiChatRef.current?.setMessage(s);
											}}
										/>
										<AiExampleQuestion
											className="mb-2"
											question="How is AI impacting the role of the IT function?"
											onClick={(s) => {
												aiChatRef.current?.setMessage(s);
											}}
										/>
										<AiExampleQuestion
											question="How is AI changing the workforce strategy and role of HR?"
											onClick={(s) => {
												aiChatRef.current?.setMessage(s);
											}}
										/>
									</>
								)}
							</>
						}
					/>
				</div>
			</div>
		</>
	);
};
